import { useEffect, useMemo, useRef, useState } from "react";
import { Archive, ArrowUpRight, Plus, Search, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import FooterNav from "../components/FooterNav";
import { getProfile } from "../api/profile";
import { fetchJoinedSpaces, fetchSpaces, joinSpace, type Space } from "../api/spaces";
import CreateSpaceModal from "../features/spaces/CreateSpaceModal";
import SpaceCommunityCard from "../features/spaces/SpaceCommunityCard";
import { readSpacesLandingMemory, rememberSpacesLandingMemory } from "../features/spaces/listMemory";
import { spacesBackState } from "../features/spaces/navigation";
import { loadAllJoinedSpaces } from "./spacesData";

function matchesSearch(space: Space, query: string) {
    const value = query.trim().toLowerCase();
    if (!value) return true;
    return [space.title, space.desc, space.category, space.by, ...(space.tags || [])]
        .filter(Boolean)
        .some((entry) => String(entry).toLowerCase().includes(value));
}

function isBlackspaceSpace(space: Space) {
    return String(space.id || space.title || "").trim().toLowerCase() === "blackspace";
}

function sortJoinedSpaces(spaces: Space[]) {
    return [...spaces].sort((a, b) => {
        if (isBlackspaceSpace(a) && !isBlackspaceSpace(b)) return -1;
        if (!isBlackspaceSpace(a) && isBlackspaceSpace(b)) return 1;
        return 0;
    });
}

function SpaceSkeleton({ compact = false }: { compact?: boolean }) {
    return <div className={`${compact ? "w-[17rem] shrink-0" : "w-full"} h-60 animate-pulse rounded-2xl border border-white/10 bg-neutral-950`} />;
}

function profileLooksPresence(profile: any) {
    const username = String(profile?.username || "").replace(/^@/, "").trim().toLowerCase();
    return Boolean(
        profile?.isPresence ||
        profile?.realm?.isPresence ||
        profile?.realm?.role === "presence" ||
        profile?.realmRole === "presence" ||
        profile?.role === "presence" ||
        username === "blackspace"
    );
}

function readPresenceHint() {
    try {
        const raw = localStorage.getItem("user") || localStorage.getItem("profile");
        if (!raw) return null;
        return profileLooksPresence(JSON.parse(raw));
    } catch {
        return null;
    }
}

export default function Spaces() {
    const navigate = useNavigate();
    const location = useLocation();
    const cachedListRef = useRef(readSpacesLandingMemory());
    const restoredScrollRef = useRef(false);
    const [joinedSpaces, setJoinedSpaces] = useState<Space[]>(() => cachedListRef.current?.joinedSpaces || []);
    const [discoverSpaces, setDiscoverSpaces] = useState<Space[]>(() => cachedListRef.current?.discoverSpaces || []);
    const [search, setSearch] = useState(() => cachedListRef.current?.search || "");
    const [loading, setLoading] = useState(() => !cachedListRef.current);
    const [error, setError] = useState<string | null>(null);
    const [showCreate, setShowCreate] = useState(false);
    const [joiningId, setJoiningId] = useState<string | null>(null);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [showAllJoined, setShowAllJoined] = useState(false);
    const [identityState, setIdentityState] = useState(() => {
        const hint = readPresenceHint();
        return { ready: hint !== null, isPresence: Boolean(hint) };
    });
    const mainRef = useRef<HTMLElement | null>(null);
    const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        let alive = true;
        getProfile()
            .then((data: any) => {
                if (!alive) return;
                setIdentityState({ ready: true, isPresence: profileLooksPresence({ ...data?.user, realm: data?.realm || data?.user?.realm }) });
            })
            .catch(() => {
                if (alive) setIdentityState((current) => ({ ...current, ready: true }));
            });
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (!mobileSearchOpen) return;
        const timer = window.setTimeout(() => mobileSearchInputRef.current?.focus(), 40);
        return () => window.clearTimeout(timer);
    }, [mobileSearchOpen]);

    useEffect(() => {
        let alive = true;
        const hasCachedList = Boolean(cachedListRef.current);
        setLoading(!hasCachedList);
        setError(null);

        async function loadSpaces() {
            try {
                const [joined, discovery] = await Promise.all([
                    loadAllJoinedSpaces((page, limit) => fetchJoinedSpaces({ page, limit, archived: false })),
                    fetchSpaces({ page: 1, limit: 18, archived: false, excludeJoined: true }),
                ]);
                if (!alive) return;
                const liveJoined = joined.spaces.filter((space) => !space.isArchived);
                setJoinedSpaces(liveJoined);
                const joinedIds = new Set(liveJoined.map((space) => space.id));
                setDiscoverSpaces(discovery.spaces.filter((space) => !space.isArchived && !joinedIds.has(space.id)));
            } catch (err: any) {
                if (alive) setError(err?.message || "Could not load communities.");
            } finally {
                if (alive) setLoading(false);
            }
        }

        void loadSpaces();

        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        rememberSpacesLandingMemory({
            joinedSpaces,
            discoverSpaces,
            search,
        });
    }, [discoverSpaces, joinedSpaces, search]);

    useEffect(() => {
        const cached = cachedListRef.current;
        if (restoredScrollRef.current || !cached?.scrollTop || !mainRef.current || (loading && !joinedSpaces.length && !discoverSpaces.length)) return;
        restoredScrollRef.current = true;
        window.requestAnimationFrame(() => {
            if (mainRef.current) mainRef.current.scrollTop = cached.scrollTop;
        });
    }, [discoverSpaces.length, joinedSpaces.length, loading]);

    function rememberScroll(scrollTop = mainRef.current?.scrollTop || 0) {
        rememberSpacesLandingMemory({ scrollTop });
    }

    function openSpace(id: string) {
        rememberScroll();
        navigate(`/spaces/${id}`, { state: spacesBackState(location, "Spaces") });
    }

    function openSpacesRoute(path: string) {
        rememberScroll();
        navigate(path, { state: spacesBackState(location, "Spaces") });
    }

    const visibleJoined = useMemo(
        () => sortJoinedSpaces(joinedSpaces.filter((space) => matchesSearch(space, search))),
        [joinedSpaces, search]
    );
    const visibleDiscover = useMemo(
        () => discoverSpaces.filter((space) => matchesSearch(space, search)),
        [discoverSpaces, search]
    );
    const isPresenceIdentity = identityState.isPresence;
    const openOnlySpaceCards = !identityState.ready || isPresenceIdentity;
    const showMembershipSection = identityState.ready && !isPresenceIdentity;
    const hasResults = visibleDiscover.length > 0 || (showMembershipSection && visibleJoined.length > 0);

    async function handleJoin(spaceId: string) {
        if (isPresenceIdentity) {
            openSpace(spaceId);
            return;
        }
        if (joiningId) return;
        setJoiningId(spaceId);
        setError(null);
        try {
            const joined = await joinSpace(spaceId);
            setJoinedSpaces((current) => [joined, ...current.filter((space) => space.id !== joined.id)]);
            setDiscoverSpaces((current) => current.filter((space) => space.id !== joined.id));
            openSpace(joined.id);
        } catch (err: any) {
            setError(err?.message || "Could not join this community.");
        } finally {
            setJoiningId(null);
        }
    }

    return (
        <div className="bs-mobile-spaces bs-spaces-community flex h-screen flex-col overflow-hidden bg-black text-white">
            <header className="bs-spaces-mobile-header sticky top-0 z-30 border-b border-neutral-800 bg-black/95 px-4 pb-3 pt-4 backdrop-blur">
                <div className="mx-auto max-w-7xl">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <h1 className="bs-message-page-title bs-spaces-mobile-title truncate">
                                    Spaces
                                    <span className="bs-spaces-title-dot" aria-hidden="true" />
                                </h1>
                                <p className="bs-spaces-mobile-subtitle">Discover communities</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2 md:hidden">
                                <button
                                    type="button"
                                    onClick={() => openSpacesRoute("/spaces/archives")}
                                    className="bs-all-spaces-command bs-all-spaces-archive bs-spaces-archive-command"
                                    aria-label="Open Space archives"
                                >
                                    <Archive size={22} strokeWidth={2.45} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMobileSearchOpen((open) => !open)}
                                    className={`bs-home-mobile-command ${mobileSearchOpen || search ? "is-active" : ""}`}
                                    aria-label="Search spaces"
                                    aria-expanded={mobileSearchOpen}
                                >
                                    <Search size={18} />
                                </button>
                                <button type="button" onClick={() => setShowCreate(true)} className="bs-home-mobile-command" aria-label="Create Space">
                                    <Plus size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="hidden flex-col gap-3 md:flex md:flex-row md:items-center">
                            <label className="bs-spaces-search-shell flex h-11 min-w-0 items-center gap-3 rounded-lg border border-neutral-800 bg-neutral-950 px-3 md:w-80 focus-within:border-cyan-700">
                                <Search size={17} className="shrink-0 text-neutral-500" />
                                <span className="sr-only">Search spaces</span>
                                <input
                                    type="search"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Search communities"
                                    className="bs-spaces-search-input min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-neutral-600"
                                />
                                {search && <button type="button" onClick={() => setSearch("")} className="text-neutral-500 hover:text-white" aria-label="Clear search"><X size={15} /></button>}
                            </label>
                            <button type="button" onClick={() => openSpacesRoute("/spaces/archives")} className="bs-all-spaces-command bs-all-spaces-archive bs-spaces-archive-command h-11 px-4 text-sm font-bold">
                                <Archive size={22} strokeWidth={2.45} /> Archives
                            </button>
                            <button type="button" onClick={() => setShowCreate(true)} className="bs-primary-action inline-flex h-11 items-center justify-center gap-2 px-4 text-sm">
                                <Plus size={17} /> Create Space
                            </button>
                        </div>
                    </div>

                    {mobileSearchOpen && (
                        <div className="bs-spaces-mobile-search-sheet mt-2 md:hidden" role="search" aria-label="Search communities">
                            <form
                                className="bs-spaces-search-shell bs-spaces-mobile-search-form flex items-center gap-2"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    mobileSearchInputRef.current?.blur();
                                }}
                            >
                                <Search size={17} className="shrink-0 text-cyan-200" />
                                <input
                                    ref={mobileSearchInputRef}
                                    type="search"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Search communities"
                                    className="bs-spaces-search-input bs-mobile-search-input min-w-0 flex-1 bg-transparent text-sm font-semibold outline-none"
                                />
                                {search && <button type="button" onClick={() => setSearch("")} className="grid h-8 w-8 place-items-center rounded-md border border-neutral-800 bg-black/45 text-neutral-400" aria-label="Clear search"><X size={15} /></button>}
                            </form>
                        </div>
                    )}
                </div>
            </header>

            <main ref={mainRef} onScroll={(event) => rememberScroll(event.currentTarget.scrollTop)} className="bs-spaces-smooth-list flex-1 overflow-y-auto">
                <div className="bs-spaces-mobile-body mx-auto max-w-7xl pb-28 pt-6">
                    {error && <div role="alert" className="mb-6 rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm text-red-100">{error}</div>}

                    {showMembershipSection && <section aria-labelledby="your-spaces-title" className="bs-spaces-joined-section mb-10">
                        <div className="bs-spaces-section-head mb-4 flex items-end justify-between gap-3">
                            <div>
                                <h2 id="your-spaces-title" className="text-lg font-black text-white">Your spaces</h2>
                                <p className="text-sm text-neutral-500">Spaces you're part of</p>
                            </div>
                            {visibleJoined.length > 6 && (
                                <button type="button" onClick={() => setShowAllJoined((value) => !value)} className="bs-spaces-view-all text-sm font-bold text-violet-200 hover:text-white">
                                    {showAllJoined ? "View less" : "View all"}
                                </button>
                            )}
                        </div>
                        <div className="bs-spaces-joined-list grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {loading && Array.from({ length: 2 }).map((_, index) => <SpaceSkeleton key={index} compact />)}
                            {!loading && (showAllJoined ? visibleJoined : visibleJoined.slice(0, 6)).map((space) => (
                                <SpaceCommunityCard key={space.id} space={space} onOpen={openSpace} authorityView />
                            ))}
                            {!loading && visibleJoined.length === 0 && (
                                <div className="bs-spaces-empty-row">
                                    {search ? "No joined Spaces match your search." : "Spaces you join will appear here."}
                                </div>
                            )}
                        </div>
                    </section>}

                    <section aria-labelledby="discover-spaces-title" className="bs-spaces-discover-section">
                        <div className="bs-spaces-section-head mb-4 flex items-end justify-between gap-3">
                            <div>
                                <h2 id="discover-spaces-title" className="text-lg font-black text-white">Discover</h2>
                                <p className="text-sm text-neutral-500">Active spaces on Blackspace</p>
                            </div>
                            <button type="button" onClick={() => openSpacesRoute("/spaces/all")} className="bs-spaces-see-live inline-flex items-center gap-1.5 text-sm font-bold text-cyan-200 hover:text-white">
                                See all live <ArrowUpRight size={15} />
                            </button>
                        </div>
                        <div className="bs-spaces-discover-list grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {loading && Array.from({ length: 4 }).map((_, index) => <SpaceSkeleton key={index} />)}
                            {!loading && visibleDiscover.slice(0, 8).map((space) => (
                                <SpaceCommunityCard
                                    key={space.id}
                                    space={space}
                                    onOpen={openSpace}
                                    onJoin={openOnlySpaceCards ? undefined : handleJoin}
                                    joining={joiningId === space.id}
                                    authorityView={openOnlySpaceCards}
                                />
                            ))}
                        </div>
                    </section>

                    {!loading && !hasResults && (
                        <div className="mt-8 rounded-2xl border border-dashed border-white/10 p-10 text-center">
                            <h2 className="font-black text-white">No communities found</h2>
                            <p className="mt-1 text-sm text-neutral-500">Try another name or clear the search.</p>
                        </div>
                    )}
                </div>
            </main>

            {showCreate && (
                <CreateSpaceModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(space) => {
                        setJoinedSpaces((current) => [space, ...current.filter((item) => item.id !== space.id)]);
                        setDiscoverSpaces((current) => current.filter((item) => item.id !== space.id));
                        rememberScroll();
                        navigate(`/spaces/${space.id}`, { state: { ...spacesBackState(location, "Spaces"), revealedAccessCode: space.inviteCode || "" } });
                    }}
                />
            )}
            <FooterNav />
        </div>
    );
}
